'use client';

// imports
import { useState, useRef, useEffect } from "react";
import { useAtom } from "jotai";

// lib files
import { isAlphaNumeric } from "@/lib/helpers/isAlphaNumeric";
import { useHasMounted } from '@/lib/hooks';
import { locateAtom, PageStatusEnum } from "@/lib/atoms/location-verification";

// svgs
import CloseIcon from '@/public/svg/close.svg';

// styles
import styles from './LocateCodeInput.module.scss';

const LOCATE_CODE_MIN_LENGTH = 6;
const LOCATE_CODE_MAX_LENGTH = 7;

enum LocateCodeErrorMessages {
  INVALID_LENGTH = `Verification code must be ${LOCATE_CODE_MIN_LENGTH} or ${LOCATE_CODE_MAX_LENGTH} characters long.`,
  ALPHANUMERIC_ONLY = "Empty spaces or special characters are not allowed.",
}

const LocateCodeInput = () => {
  const [ locateCodeInputIsAtMinLength, setLocateCodeInputIsAtMinLength ] = useState(false);
  const [ locateState, setLocateState ] = useAtom(locateAtom);

  const inputRef = useRef<HTMLInputElement>(null);
  const hasMounted = useHasMounted();

  // support for the ?code= query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // only get the specified query param
    // Note the localization API will generate QR codes for the client applications
    // that will point at ?code=ABC123 for the locate flow
    const locateCodeQueryParam = params.get("code");

    if (
        hasMounted &&
        inputRef.current &&
        locateCodeQueryParam &&
        isAlphaNumeric(locateCodeQueryParam) &&
        locateCodeQueryParam.length >= LOCATE_CODE_MIN_LENGTH &&
        locateCodeQueryParam.length <= LOCATE_CODE_MAX_LENGTH
    ) {
        setLocateCodeInputIsAtMinLength(true);
        inputRef.current.value = locateCodeQueryParam.toLocaleUpperCase();
    } else if (
        hasMounted &&
        inputRef.current &&
        locateState.devLocateCode !== null
      ) {
        setLocateCodeInputIsAtMinLength(true);
        inputRef.current.value = locateState.devLocateCode;
      }
  }, [hasMounted, locateState.devLocateCode]);

  useEffect(() => {
    if (hasMounted && inputRef.current && locateState.locateCode !== null) {
      inputRef.current.value = locateState.locateCode;
      setLocateCodeInputIsAtMinLength(locateState.locateCode.length >= LOCATE_CODE_MIN_LENGTH);
    }
  }, [hasMounted, locateState.locateCode]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const locateCode = (e.target as HTMLFormElement).locateCode.value;

    // Client side validation
    switch(true) {
      // Does it contain special characters?
      case !isAlphaNumeric(locateCode):
        setLocateState({
          ...locateState,
          locateCodeError: LocateCodeErrorMessages.ALPHANUMERIC_ONLY,
        });
        break;
      // Is it not the correct length?
      case locateCode.length < LOCATE_CODE_MIN_LENGTH || locateCode.length > LOCATE_CODE_MAX_LENGTH:
        setLocateState({
          ...locateState,
          locateCodeError: LocateCodeErrorMessages.INVALID_LENGTH,
        });
        break;
      // If none of the above, submit the locateCode
      default:
        setLocateState({
          ...locateState,
          locateCode: locateCode.toLocaleUpperCase(),
          locateCodeError: null,
          pageStatus: PageStatusEnum.locateCodeSubmitLoading,
        });
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocateCodeInputIsAtMinLength(e.target.value.length >= LOCATE_CODE_MIN_LENGTH);
  };

  if (!hasMounted) {
    return null;
  }

  return (
    <div className={styles.locate_code_input}>
      <h1 className={styles.locate_code_input__headline}>Enter your code</h1>
      <p className={styles.locate_code_input__text}>
        Enter the code displayed on your TV to authorize viewing your station&rsquo;s livestream on the PBS App.
      </p>

      <form action="" onSubmit={handleSubmit} className={styles.locate_code_input__form}>
        <label htmlFor="locateCode" className="visuallyhidden">Verification Code</label>
        <input
          type="text"
          name="locateCode"
          id="locateCode"
          required
          placeholder="Verification Code"
          onChange={handleChange}
          ref={inputRef}
          className={styles.locate_code_input__input}
          maxLength={7}
          // Note: the autoComplete attribute does _not_ prevent browsers from/
          // trying to autofill the input.
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={!locateCodeInputIsAtMinLength}
          className={styles.locate_code_input__button}
        >
          Continue
        </button>

        {locateState.locateCodeError && (
          <p
            className={styles.locate_code_error}
          >
          <CloseIcon />
          {locateState.locateCodeError}
          </p>
        )}

      </form>

    </div>
  );
};

export default LocateCodeInput;
